import { useCallback, useContext, useMemo, useRef } from 'react';

import { Trans } from '@lingui/react/macro';

import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalPatientContext, ONBOARDING_VIDEO_KEY } from '../constants';

const WEBINAR_URL =
  'https://videahealth.ewebinar.com/webinar/ascend-detectai-by-videahealth-onboarding-23237/join';

export const Onboarding = () => {
  const { trackEvent } = useTrackEvent();
  const externalPatientContext = useContext(ExternalPatientContext);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSelectLink = useCallback(() => {
    inputRef.current?.select();
    trackEvent(mixpanelEvents.externalOnboardingWebinarLinkSelect, {
      ...externalPatientContext,
    });
  }, [trackEvent, externalPatientContext]);

  // Check if MP4 is supported first, then fall back to WebM
  const canPlayMP4 = useMemo(() => {
    const video = document.createElement('video');
    return (
      video.canPlayType('video/mp4; codecs="avc1.42E01E, mp4a.40.2"') ===
        'probably' ||
      video.canPlayType('video/mp4; codecs="avc1.42E01E, mp4a.40.2"') ===
        'maybe' ||
      video.canPlayType('video/mp4; codecs="avc1.4D001E, mp4a.40.2"') ===
        'probably' ||
      video.canPlayType('video/mp4; codecs="avc1.4D001E, mp4a.40.2"') ===
        'maybe'
    );
  }, []);

  const handlePlay = () => {
    localStorage.setItem(ONBOARDING_VIDEO_KEY, 'true');
    trackEvent(mixpanelEvents.externalOnboardingVideoPlay, {
      ...externalPatientContext,
    });
  };

  return (
    <div className="vai-flex vai-flex-col vai-gap-4">
      <p className="vai-font-merriweather vai-font-bold vai-text-3xl vai-text-slate-900 vai-text-center">
        <Trans>Welcome to Detect AI, powered by VideaHealth</Trans>
      </p>

      <div className="vai-flex vai-justify-between vai-gap-4 vai-items-start">
        <div className="vai-flex vai-flex-col vai-gap-2 vai-shrink-0">
          <div className="vai-flex vai-items-center vai-gap-3">
            <span className="vai-flex vai-items-center vai-justify-center vai-w-6 vai-h-6 vai-rounded-full vai-bg-blue-600 vai-text-white vai-text-xs vai-font-bold vai-shrink-0">
              1
            </span>
            <p className="vai-font-inter vai-text-sm vai-text-slate-700">
              <Trans>
                Watch this quick 1-minute video to get started in no time.
              </Trans>
            </p>
          </div>
          <div className="vai-flex vai-items-center vai-gap-3">
            <span className="vai-flex vai-items-center vai-justify-center vai-w-6 vai-h-6 vai-rounded-full vai-bg-blue-600 vai-text-white vai-text-xs vai-font-bold vai-shrink-0">
              2
            </span>
            <p className="vai-font-inter vai-text-sm vai-text-slate-700">
              <Trans>
                Train your team with the in-depth, interactive 20-minute
                onboarding webinar.
              </Trans>
            </p>
          </div>
        </div>

        <div className="vai-bg-blue-50 vai-rounded-lg vai-px-4 vai-py-3 vai-border-l-4 vai-border-blue-600 vai-min-w-0 vai-max-w-[360px]">
          <p className="vai-font-inter vai-font-bold vai-text-sm vai-text-slate-900 vai-flex vai-items-center vai-gap-2">
            <span className="vai-flex vai-items-center vai-justify-center vai-w-5 vai-h-5 vai-rounded-full vai-bg-blue-600 vai-text-white vai-text-[10px] vai-font-bold vai-shrink-0">
              2
            </span>
            <Trans>Want More Training?</Trans>
          </p>
          <p className="vai-font-inter vai-text-sm vai-text-slate-500 vai-mt-1">
            <Trans>
              Click the link to select it, then press Ctrl+C or ⌘+C to copy.
            </Trans>
          </p>
          <input
            ref={inputRef}
            readOnly
            value={WEBINAR_URL}
            onClick={handleSelectLink}
            className="vai-w-full vai-text-sm vai-text-blue-600 vai-bg-transparent vai-border-0 vai-p-0 vai-mt-1 vai-cursor-text vai-truncate focus:vai-outline-none"
          />
        </div>
      </div>

      <hr className="vai-border-slate-200" />

      <div>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          onPlay={handlePlay}
          controls
          playsInline
          preload="metadata"
          className="vai-w-full vai-rounded-lg"
        >
          {canPlayMP4 ? (
            <source
              src="https://videahealth-public-assets.s3.us-east-2.amazonaws.com/detectai-onboarding.mp4"
              type="video/mp4"
            />
          ) : (
            <source
              src="https://videahealth-public-assets.s3.us-east-2.amazonaws.com/detectai-onboarding.webm"
              type="video/webm"
            />
          )}
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
  );
};
